/**
 * A3 (2026-06-27) — Customer Sign-up (web).
 *
 * Single-identifier field "Email or Phone Number" (auto-detect) +
 * Password + Confirm Password + Google + Apple. Full Name moved to
 * the Complete Profile step. Posts to /api/auth/register and routes
 * via the gated router on success → /customer/complete-profile.
 *
 * Backend contract: /api/auth/register REQUIRES email + name
 * (A1-sealed). Phone-only sign-up isn't supported server-side; we
 * surface an honest inline hint directing phone users to Google /
 * Apple above instead of inventing fake emails.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { SocialAuthButtons } from '@/components/customer/SocialAuthButtons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function looksLikePhone(s) {
  if (!s) return false;
  if (s.includes('@')) return false;
  const digits = s.replace(/[^\d]/g, '');
  return digits.length >= 7;
}

export default function CustomerSignUpEmailPage() {
  const signInWithToken = useAuthStore((s) => s.signInWithToken);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const idRef = useRef(null);
  useEffect(() => {
    if (idRef.current) idRef.current.focus();
  }, []);

  // Already-authenticated customers bypass this page via the gated
  // router. (Preserves pre-A3 Navigate behaviour for the common case
  // where someone bookmarks /customer/signup.)
  if (!loading && token && user?.role === 'customer' && user?.profile_completed) {
    return <Navigate to="/customer/home" replace />;
  }

  const idTrim = identifier.trim();
  const isEmail = idTrim.length > 0 && idTrim.includes('@');
  const isPhoneOnly = idTrim.length > 0 && !isEmail && looksLikePhone(idTrim);
  const canSubmit =
    !submitting &&
    isEmail &&
    EMAIL_RE.test(idTrim) &&
    password.length >= 6 &&
    password === passwordConfirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (isPhoneOnly) {
      setErr({
        title: 'Phone-only sign-up unavailable',
        message:
          'Please enter an email, or use the Google / Apple buttons above.',
      });
      return;
    }
    if (!isEmail || !EMAIL_RE.test(idTrim)) {
      setErr({ title: 'Invalid email', message: 'Please enter a valid email address.' });
      return;
    }
    if (password.length < 6) {
      setErr({ title: 'Password too short', message: 'Password must be at least 6 characters.' });
      return;
    }
    if (password !== passwordConfirm) {
      setErr({ title: 'Passwords do not match', message: 'Please re-enter the same password.' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.customerRegister({
        // Placeholder — the real full name is captured in the next
        // step (Complete Profile), which overwrites `name` on the
        // user doc. Backend A1 requires a non-empty name on register.
        name:     'New Member',
        email:    idTrim.toLowerCase(),
        password,
        phone:    null,
      });
      await signInWithToken(res); // runs the gated router → Complete Profile
    } catch (e2) {
      if (e2?.status === 409) {
        setErr({
          title: 'Email already registered',
          message: 'This email is already registered.',
        });
      } else {
        setErr({
          title: 'Unable to create your account',
          message:
            typeof e2?.detail === 'string' && e2.detail
              ? e2.detail
              : 'Unable to create your account.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSocialError = (msg) => {
    setErr({ title: 'Social sign-in failed', message: msg });
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center px-4 py-10"
      data-testid="customer-signup-page"
    >
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <img src="/onestamp-logo.png" alt="OneStamp" className="w-12 h-12 rounded-2xl" />
          <div className="text-xs text-gold uppercase tracking-widest font-semibold">
            OneStamp
          </div>
        </div>

        <div className="rounded-3xl bg-[#15151B] border border-white/8 p-7 shadow-2xl">
          <h1 className="font-display text-2xl font-semibold mb-2">
            Create account
          </h1>
          <p className="text-sm text-white/55 mb-6">
            Enter your details — we'll ask for your full name on the next step.
          </p>

          {err ? (
            <div
              className="mb-5 px-3 py-2.5 rounded-xl bg-rose-500/8 border border-rose-400/25 flex items-start gap-2 text-xs"
              data-testid="customer-signup-error-band"
              role="alert"
            >
              <AlertCircle size={14} className="text-rose-300 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-rose-200">{err.title}</div>
                <div className="text-rose-200/80 mt-0.5">{err.message}</div>
              </div>
            </div>
          ) : null}

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="customer-signup-form"
            noValidate
          >
            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Email or phone number
              </label>
              <input
                type="text"
                autoComplete="username"
                value={identifier}
                ref={idRef}
                onChange={(e) => setIdentifier(e.target.value)}
                data-testid="customer-signup-identifier"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="you@example.com or +62 812 …"
              />
              {/* Hidden legacy testID mirror */}
              <input
                type="hidden"
                value={identifier}
                data-testid="customer-signup-email-emailfield"
                readOnly
              />
              {isPhoneOnly ? (
                <div
                  className="mt-2 text-[11px] text-white/55"
                  data-testid="customer-signup-phone-hint"
                >
                  Phone-only sign-up is available via Google or Apple below.
                  Password sign-up requires an email address.
                </div>
              ) : null}
            </div>

            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="customer-signup-password"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="At least 6 characters"
              />
            </div>

            <div>
              <label className="block text-xs font-mono text-white/55 mb-1.5 uppercase tracking-wider">
                Confirm password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                data-testid="customer-signup-password-confirm"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-gold/40 focus:bg-black/40 transition-colors"
                placeholder="Re-enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="customer-signup-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-xl py-2.5 text-sm hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-glow-gold-sm"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Creating account…
                </>
              ) : (
                'Create account'
              )}
            </button>
          </form>

          <SocialAuthButtons
            busy={submitting}
            onSuccess={async (res) => {
              setErr(null);
              await signInWithToken(res);
            }}
            onError={onSocialError}
          />

          <div className="mt-6 text-center text-xs text-white/55">
            Already a member?{' '}
            <Link
              to="/customer/login"
              data-testid="customer-signup-login-link"
              className="text-gold font-semibold hover:text-gold-light"
            >
              Sign in
            </Link>
          </div>

          <div className="mt-8 text-center text-[11px] text-white/35">
            <Link
              to="/privacy"
              data-testid="customer-signup-privacy-link"
              className="hover:text-white/70 transition-colors"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
